import { CarouselModal } from '@/components/common/carousel-modal';
import { PoiListCarousel } from '@/components/feature/floor-guide/poi-list-carousel';
import shopStyles from '@/components/feature/shop/shop-poi-modal/styles.module.scss';
import type { PoiContent } from '@/services/dto';
import { useConfigStore } from '@/stores';
import type { PoiContentSlide } from '@/types/shop';
import { formatPoiSlideToDetail } from '@/utils/floor-guide/formatPoiSlideToDetail';
import clsx from 'clsx';
import { useMemo } from 'react';
import { buildFloorGuidePoiDetailTabOrders } from './buildTabOrders';
import { FloorGuidePoiDetailVertical } from './poi-detail-vertical';

type Props = {
    isOpen: boolean;
    language: string;
    poiContent: PoiContentSlide | null;
    relatedPoiContents?: PoiContent[];
    selectedPoiContentId?: string;
    screenMode: 'split' | 'fullscreen';
    onSelectPoiContent?: (poi: PoiContent) => void;
    onClose: () => void;
    onGuideStart: () => void;
};

export const FloorGuideVerticalPoiModal = ({
    isOpen,
    language,
    poiContent,
    relatedPoiContents = [],
    selectedPoiContentId,
    screenMode,
    onSelectPoiContent,
    onClose,
    onGuideStart,
}: Props) => {
    const contrast = useConfigStore((s) => s.config?.ui?.contrast);

    const poiDetail = useMemo(() => formatPoiSlideToDetail(poiContent, language), [poiContent, language]);
    const showContentCarousel = relatedPoiContents.length > 1;
    const carouselCount = showContentCarousel ? relatedPoiContents.length : 0;
    const tabOrders = useMemo(
        () => (poiDetail ? buildFloorGuidePoiDetailTabOrders(poiDetail, { carouselCount }) : null),
        [poiDetail, carouselCount]
    );

    const modalSurfaceClassName = clsx(shopStyles['poi-modal'], shopStyles['poi-modal__vertical'], {
        [shopStyles['poi-modal__vertical--halfmode']]: screenMode === 'split',
        [shopStyles['poi-modal__contrast']]: contrast,
    });

    if (!poiDetail || !tabOrders) {
        return null;
    }

    const detailBody = (
        <FloorGuidePoiDetailVertical
            poiDetail={poiDetail}
            language={language}
            onGuideStart={onGuideStart}
            carouselCount={carouselCount}
        />
    );

    return (
        <CarouselModal
            isOpen={isOpen}
            onClose={onClose}
            closeIconTabIndex={tabOrders.close}
            closeIconRegionOrder={tabOrders.close}
            closePlacement="below-right"
            contentClassName={modalSurfaceClassName}
            slides={
                showContentCarousel ? (
                    <PoiListCarousel
                        poiList={relatedPoiContents}
                        onSelectPoi={onSelectPoiContent}
                        selectedPoiId={selectedPoiContentId}
                        tabOrders={tabOrders.carousel}
                        alignLeft
                    />
                ) : undefined
            }
        >
            {detailBody}
        </CarouselModal>
    );
};
